import DownstreamSync from "../components/DownstreamSync";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProject } from "../context/ProjectContext";
import { STAGE_TAB, OPTIONAL_STAGES } from "../lib/flow";
import { api, fmtErr } from "../lib/api";
import { daysSince, PRIORITY_COLORS } from "../lib/constants";
import IndustryBadge from "../components/IndustryBadge";
import { EquipmentRollup, EquipmentSwitcher } from "../components/EquipmentSwitcher";
import ProjectScm from "../components/ProjectScm";
import TeamCard from "../components/TeamCard";
import BomWorkspace from "../components/bom/BomWorkspace";
import RevisionsTab from "../components/RevisionsTab";
import CommentsThread from "../components/CommentsThread";
import IntakePanel from "../components/intake/IntakePanel";
import DesignEngine from "./DesignEngine";
import ValidationSuite from "./Validation";
import DrawingStudioPage from "./DrawingStudioPage";
import DocumentCenter from "./Documents";
import { ArrowLeft, CaretDown, CaretUp, Check, CheckCircle, FilePdf, Scroll } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

const TABS = ["Overview", "Specs", "Intake", "Design", "Validation", "Documents", "Drawings", "BOM", "SCM", "Revisions", "Activity"];
const TAB_LABEL = { design: "Design Engine", drawings: "Drawing Studio", documents: "Documents" };
const TAB_MODULES = { design: "design", validation: "validation", documents: "documents", drawings: "drawings", bom: "bom", scm: "scm" };

function WorkflowBanner({ pid, project, onChanged }) {
  const [st, setSt] = useState(null);
  const [revOpen, setRevOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    api.get(`/projects/${pid}/workflow-status`).then((r) => setSt(r.data)).catch(() => {});
  }, [pid, project?.stage, project?.ready_to_advance]);
  if (!st) return null;
  const stages = user?.org?.workflow_stages || [];
  const idx = stages.findIndex((s) => s.key === st.stage);
  const next = stages[idx + 1];
  const canAct = ["engineer", "lead", "project_manager", "admin"].includes(user?.role);
  const canReverse = ["lead", "project_manager", "admin"].includes(user?.role);

  const advance = async () => {
    setBusy(true);
    try { await api.post(`/projects/${pid}/advance`); toast.success(`Advanced to ${next?.name}`); onChanged(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const reverse = async () => {
    setBusy(true);
    try { await api.post(`/projects/${pid}/reverse`, { reason }); toast.success("Stage reversed"); setRevOpen(false); setReason(""); onChanged(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-2" data-testid="workflow-banner-wrap">
      {(st.ready_to_advance || st.criteria_met) && next && (
        <div className="bg-[var(--c-green)]/10 border border-[var(--c-green)]/40 rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="ready-banner">
          <CheckCircle size={16} weight="fill" className="text-[var(--c-green)] shrink-0" />
          <span className="text-xs text-[var(--c-text)]">
            Stage <b>{stages[idx]?.name}</b> exit criteria met ({st.detail}). Ready to advance to <b>{next.name}</b>.
          </span>
          {canAct && (
            <button data-testid="advance-stage-btn" onClick={advance} disabled={busy}
              className="h-6 px-2.5 bg-[var(--c-green)] text-white text-[10px] rounded-sm disabled:opacity-50">
              Advance to {next.name} →
            </button>
          )}
        </div>
      )}
      {canReverse && idx > 0 && (
        <div className="flex justify-end">
          {!revOpen ? (
            <button data-testid="reverse-stage-btn" onClick={() => setRevOpen(true)}
              className="text-[10px] text-[var(--c-muted)] hover:text-[var(--c-amber)]">← Return to {stages[idx - 1]?.name}</button>
          ) : (
            <div className="flex items-center gap-2" data-testid="reverse-form">
              <input data-testid="reverse-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (required)…"
                className="w-56 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[10px] rounded-sm px-2" />
              <button data-testid="reverse-confirm" onClick={reverse} disabled={busy || reason.trim().length < 5}
                className="h-6 px-2 bg-[var(--c-amber)] text-white text-[10px] rounded-sm disabled:opacity-40">Return</button>
              <button onClick={() => setRevOpen(false)} className="text-[10px] text-[var(--c-muted)]">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlaceholderTab({ name }) {
  return (
    <div className="border border-dashed border-[var(--c-border)] rounded-sm p-10 text-center" data-testid={`tab-placeholder-${name.toLowerCase()}`}>
      <div className="text-sm text-[var(--c-text)] font-medium">{name}</div>
      <div className="text-xs text-[var(--c-muted)] mt-1.5">This module's content is populated in an upcoming build prompt. The shell, routing and industry context are ready.</div>
    </div>
  );
}

function TenderPackButton({ projectId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data, headers } = await api.get(`/projects/${projectId}/tender-pack.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success(`Tender pack ready — ${headers["x-pack-pages"] || "?"} pages`);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let msg = fmtErr(e);
      if (e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep */ }
      }
      toast.error(msg);
    }
    setBusy(false);
  };
  return (
    <button onClick={download} disabled={busy} data-testid="tender-pack-btn"
      className="order-last ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-[var(--c-accent)]/50 bg-[var(--c-accent)]/10 text-[var(--c-text)] hover:bg-[var(--c-accent)]/20 transition-colors duration-150 disabled:opacity-50">
      <FilePdf size={14} /> {busy ? "Building…" : "Combined Tender Pack"}
    </button>
  );
}

const VariantGate = ({ projectId, tabKey }) => {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/finalize-status`).then(({ data }) => setSt(data)).catch(() => {});
  }, [projectId]);
  if (!st || st.count < 2) return null;
  const active = (st.variants || []).find((v) => v.active);
  if (!active || active.finalized) return null;
  return (
    <div className="mb-4 flex items-start gap-3 rounded-sm border border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10 px-4 py-3"
      data-testid={`variant-gate-${tabKey}`}>
      <span className="text-[var(--c-amber)] text-sm">⚠</span>
      <div className="text-[11px] text-[var(--c-text)] leading-relaxed">
        Variant <span className="font-mono text-[var(--c-accent)]">{active.tag}</span> is not finalized —
        this project has {st.count} variants and only finalized variants run the Design Engine, Validation
        and Drawing Studio.
        {active.missing?.length ? ` ${active.missing.length} critical field(s) still empty.` : ""}
        <a href={`/projects/${projectId}?tab=intake`} data-testid={`variant-gate-link-${tabKey}`}
          className="ml-1 text-[var(--c-accent)] hover:underline">Finalize it in Intake → Finalize</a>.
      </div>
    </div>
  );
};

const EquipmentRollupSlot = ({ projectId }) => (
  <div className="mb-4"><EquipmentRollup projectId={projectId} /></div>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { pid, setPid, reload: reloadProjects } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [pack, setPack] = useState(null);
  const [panelOpen, setPanelOpen] = useState(() => localStorage.getItem("fg_proj_panel") !== "0");
  const togglePanel = () => setPanelOpen((v) => { localStorage.setItem("fg_proj_panel", v ? "0" : "1"); return !v; });
  const userModules = user?.modules; // undefined on stale sessions -> fail open
  const visibleTabs = TABS.filter((t) => !TAB_MODULES[t.toLowerCase()] || user?.role === "admin" || !userModules || userModules.includes(TAB_MODULES[t.toLowerCase()]));
  const tabParam = (searchParams.get("tab") || "").toLowerCase();
  const tab = visibleTabs.find((t) => t.toLowerCase() === tabParam)?.toLowerCase() || "overview";

  const setTab = (next) => {
    const p = new URLSearchParams(searchParams);
    if (!next || next === "overview") p.delete("tab"); else p.set("tab", next);
    setSearchParams(p);
  };

  // this project is the workspace-wide active project while we are on its page
  useEffect(() => { if (id && id !== pid) setPid(id); }, [id]); // eslint-disable-line

  const load = () => {
    api.get(`/projects/${id}`)
      .then(async ({ data }) => {
        setProject(data);
        const packs = await api.get("/industry-packs");
        setPack(packs.data.find((p) => p.key === data.industry) || null);
      })
      .catch((e) => { toast.error(fmtErr(e)); nav("/projects"); });
  };
  useEffect(() => { load(); }, [id, nav]); // eslint-disable-line

  // Intake finalize already advances the stage server-side → land on Design
  const afterFinalize = () => {
    load();
    reloadProjects();
    setTab("design");
  };

  if (!project) return <div className="p-6 font-mono text-xs text-[var(--c-muted)] tracking-widest" data-testid="project-loading">LOADING PROJECT…</div>;

  const stages = project.workflow_stages || [];
  const fieldCount = pack ? (pack.sections || []).reduce((a, s) => a + (s.fields || []).length, 0) : 0;
  const currentIdx = stages.findIndex((s) => s.key === project.stage);

  return (
    <div className="p-6" data-testid="project-detail-page">
      <div className="flex items-center justify-between gap-3 mb-3">
        <button onClick={() => nav("/projects")} data-testid="back-to-projects"
          className="flex items-center gap-1.5 text-xs text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors duration-150">
          <ArrowLeft size={13} /> Projects
        </button>
        <button onClick={togglePanel} data-testid="toggle-project-panel"
          className="flex items-center gap-1.5 text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)] border border-[var(--c-border)] rounded-sm px-2 py-1 transition-colors duration-150">
          {panelOpen ? <><CaretUp size={12} /> Minimise panel</> : <><CaretDown size={12} /> Project details</>}
        </button>
      </div>

      {panelOpen && (
      <>
      <EquipmentRollupSlot projectId={project.id} />
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <TenderPackButton projectId={project.id} />
          <div className="flex-1 min-w-64">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xs tracking-wider text-[var(--c-muted)]" data-testid="project-code">{project.project_code}</span>
              <IndustryBadge industryKey={project.industry} />
              {project.equipment_type_name && (
                <span data-testid="project-equipment-type"
                  className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10 text-[var(--c-text)]">
                  {project.equipment_type_name}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--c-muted)]">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[project.priority] }} />
                {project.priority} priority
              </span>
            </div>
            <h1 className="text-xl tracking-tight font-medium text-[var(--c-text)] mt-2" data-testid="project-title">{project.product_summary}</h1>
            <div className="mt-2">
              <EquipmentSwitcher projectId={project.id} onSwitched={() => window.location.reload()} />
            </div>
            <div className="text-xs text-[var(--c-muted)] mt-1.5">
              {project.client_name} · Engineer: <span className="text-[var(--c-text)]">{project.engineer_name}</span> · {daysSince(project.created_at)} days active
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0 mt-5 overflow-x-auto pb-1" data-testid="progress-bar">
          {stages.map((s, i) => (
            <div key={s.key} className="flex items-center shrink-0">
              <button type="button" onClick={() => setTab(STAGE_TAB[s.key] || "overview")}
                data-testid={`stage-link-${s.key}`} title={`Open ${s.name}`}
                className="flex items-center gap-1.5 rounded-sm px-1 py-0.5 hover:bg-[var(--c-surface-2)] transition-colors duration-150">
                <div className={`w-5 h-5 rounded-sm flex items-center justify-center text-[9px] font-mono border ${
                  i < currentIdx ? "bg-[var(--c-green)] border-[var(--c-green)] text-[var(--c-text)]" :
                  i === currentIdx ? "bg-[var(--c-accent)] border-[var(--c-accent)] text-[var(--c-text)]" :
                  "border-[var(--c-border)] text-[var(--c-muted)]"}`}
                  data-testid={`stage-chip-${s.key}`}>
                  {i < currentIdx ? <Check size={10} weight="bold" /> : i + 1}
                </div>
                <span className={`text-[10px] whitespace-nowrap ${i === currentIdx ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"}`}>
                  {s.name}{OPTIONAL_STAGES.includes(s.key) ? " (optional)" : ""}
                </span>
              </button>
              {i < stages.length - 1 && <div className={`w-5 h-px mx-1.5 ${i < currentIdx ? "bg-[var(--c-green)]" : "bg-[var(--c-border)]"}`} />}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3" data-testid="progress-pct-bar">
          <div className="flex-1 h-1.5 bg-[var(--c-bg)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--c-accent)] rounded-full transition-all duration-500"
              style={{ width: `${stages.length > 1 ? Math.round((currentIdx / (stages.length - 1)) * 100) : 0}%` }} />
          </div>
          <span className="text-[10px] font-mono text-[var(--c-muted)] shrink-0" data-testid="progress-pct-label">
            {stages.length > 1 ? Math.round((currentIdx / (stages.length - 1)) * 100) : 0}% · {stages[currentIdx]?.name || project.stage}
          </span>
        </div>
      </div>

      <WorkflowBanner pid={id} project={project} onChanged={load} />
      <DownstreamSync project={project} onChanged={load} />

      {(project.rules_snapshot || []).length > 0 && (
        <div className="mt-4 bg-[var(--c-accent)]/10 border border-[var(--c-accent)]/40 rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="active-rules-banner">
          <Scroll size={16} weight="fill" className="text-[var(--c-accent)] shrink-0" />
          <span className="text-xs text-[var(--c-text)]">
            <span className="font-mono font-medium">{project.rules_snapshot.length}</span> client rules active for <span className="font-medium">{project.client_name}</span>
          </span>
          <span className="text-[10px] font-mono tracking-wider text-[var(--c-muted)] uppercase">
            {project.rules_snapshot.filter((r) => r.priority === "override").length} override · {project.rules_snapshot.filter((r) => r.priority === "supplement").length} supplement · {project.rules_snapshot.filter((r) => r.priority === "advisory").length} advisory
          </span>
        </div>
      )}
      </>
      )}

      <Tabs value={tab} onValueChange={setTab} className={panelOpen ? "mt-5" : "mt-1"}>
        <TabsList className="bg-[var(--c-surface)] border border-[var(--c-border)] flex-wrap h-auto" data-testid="project-tabs">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t} value={t.toLowerCase()} data-testid={`tab-${t.toLowerCase()}`}
              className="data-[state=active]:bg-[var(--c-border)] data-[state=active]:text-[var(--c-text)] text-[var(--c-muted)] text-xs">
              {TAB_LABEL[t.toLowerCase()] || t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="overview-tab">
            <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Project</div>
              {[["Client", project.client_name], ["Industry", project.industry.replace(/_/g, " ")],
                ["Current stage", stages[currentIdx]?.name || project.stage],
                ["Days in stage", daysSince(project.stage_entered_at)],
                ["Created", String(project.created_at || "").slice(0, 10)]].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-[var(--c-border)]/50 last:border-0 text-xs">
                  <span className="text-[var(--c-muted)]">{k}</span><span className="text-[var(--c-text)]">{v}</span>
                </div>
              ))}
            </div>
            <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Industry Pack</div>
              {pack ? (
                <>
                  {[["Spec parameters", fieldCount], ["Calculation modules", pack.calc_modules?.length ?? 0],
                    ["Validation domains", pack.validation_domains?.length ?? 0], ["Drawing types", pack.drawing_types?.length ?? 0],
                    ["Standards", (pack.standards || []).join(", ") || "—"]].map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1.5 border-b border-[var(--c-border)]/50 last:border-0 text-xs">
                      <span className="text-[var(--c-muted)]">{k}</span><span className="text-[var(--c-text)] font-mono text-right">{v}</span>
                    </div>
                  ))}
                </>
              ) : <div className="text-xs text-[var(--c-muted)]">Pack not loaded.</div>}
            </div>
            <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">SLA</div>
              {stages.map((s, i) => (
                <div key={s.key} className="flex justify-between py-1.5 border-b border-[var(--c-border)]/50 last:border-0 text-xs">
                  <span className={i === currentIdx ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"}>{s.name}</span>
                  <span className="font-mono text-[var(--c-muted)]">{s.sla_days}d</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <TeamCard projectId={id} team={project.assigned_team || []}
              onChange={(team) => setProject((p) => ({ ...p, assigned_team: team }))} />
          </div>
        </TabsContent>

        <TabsContent value="specs" className="mt-4">
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="specs-tab">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-4">
              Specification Schema · {pack?.name || "—"} · {fieldCount} parameters
            </div>
            {pack ? (
              <div className="space-y-6">
                {(pack.sections || []).map((s) => (
                  <div key={s.id} data-testid={`spec-section-view-${s.id}`}>
                    <div className="text-xs font-medium text-[var(--c-text)] mb-2.5 border-b border-[var(--c-border)] pb-1.5">{s.name}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {s.fields.map((f) => {
                        const v = project.specs?.[f.id];
                        const display = f.type === "boolean" ? (v ? "Yes" : "No")
                          : Array.isArray(v) ? v.join(", ")
                          : (v ?? null);
                        return (
                          <div key={f.id} className="border border-[var(--c-border)] rounded-sm p-3" data-testid={`spec-field-${f.id}`}>
                            <div className="text-xs font-medium text-[var(--c-text)]">
                              {f.label} {f.required && <span className="text-[var(--c-red)]">*</span>}
                            </div>
                            <div className="font-mono text-xs text-[var(--c-accent)] mt-1">
                              {display ?? <span className="text-[var(--c-muted)]">—</span>}
                              {f.unit && display != null && <span className="text-[var(--c-muted)] ml-1">{f.unit}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="text-xs text-[var(--c-muted)]">No industry pack found.</div>}
          </div>
        </TabsContent>

        {visibleTabs.slice(2).map((t) => (
          <TabsContent key={t} value={t.toLowerCase()} className="mt-4">
            {t === "Intake" ? <IntakePanel projectId={id} pack={pack} initialSpecs={project.specs} onFinalized={afterFinalize} />
            : t === "SCM" ? <ProjectScm projectId={id} />
              : t === "Design" ? (tab === "design"
                ? <><VariantGate projectId={id} tabKey="design" /><DesignEngine embedded /></> : null)
              : t === "Validation" ? (tab === "validation"
                ? <><VariantGate projectId={id} tabKey="validation" /><ValidationSuite embedded /></> : null)
              : t === "Documents" ? (tab === "documents" ? <DocumentCenter embedded /> : null)
              : t === "Drawings" ? (tab === "drawings"
                ? <><VariantGate projectId={id} tabKey="drawings" /><DrawingStudioPage embedded /></> : null)
              : t === "BOM" ? <BomWorkspace projectId={id} me={user} />
              : t === "Revisions" ? <RevisionsTab projectId={id} />
              : t === "Activity" ? <CommentsThread pid={id} user={user} />
              : <PlaceholderTab name={t} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
